import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type FocusEvent, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/*
  A caixa de dica da HUD (techspec-tooltips-e-efeitos, protótipo docs/prototipos/tooltips-magias-buffs-prototipo.html):
  no lugar do `title` nativo, uma caixa no estilo da HUD, acima do elemento (vira para baixo perto do topo).
  Mouse: abre em 250 ms e some ao sair. Teclado: abre no foco. Toque (quando o elemento pede): toque longo abre,
  o clique que vem depois é engolido, e o próximo toque em qualquer lugar fecha.
  Só uma caixa por vez. O conteúdo é uma função: a caixa repinta a cada segundo (o tempo do efeito corre).
*/

type Render = () => ReactNode;
interface Estado { readonly el: HTMLElement | null; readonly render: Render | null; readonly v: number }

let estado: Estado = { el: null, render: null, v: 0 };
const ouvintes = new Set<() => void>();
const avisar = () => { for (const f of ouvintes) f(); };

export function abrirDica(el: HTMLElement, render: Render): void {
  estado = { el, render, v: estado.v + 1 };
  avisar();
}

/** Fecha. Com `el`, só se a caixa aberta for a dele (sair de um não fecha a de outro). */
export function fecharDica(el?: HTMLElement | null): void {
  if (!estado.el || (el && estado.el !== el)) return;
  estado = { el: null, render: null, v: estado.v + 1 };
  avisar();
}

const ATRASO_MOUSE = 250;
const TOQUE_LONGO = 450;

export interface HandlersDaDica {
  onPointerEnter(e: PointerEvent<HTMLElement>): void;
  onPointerLeave(e: PointerEvent<HTMLElement>): void;
  onFocus(e: FocusEvent<HTMLElement>): void;
  onBlur(e: FocusEvent<HTMLElement>): void;
}

/**
 * Os eventos que abrem a caixa. `render` null: nada (o elemento fica sem dica). `toque`: o toque longo abre (não use
 * onde o toque longo já faz outra coisa, como o slot da barra).
 */
export function useDica(render: Render | null, { toque = false }: { toque?: boolean } = {}): HandlersDaDica & {
  onPointerDown?(e: PointerEvent<HTMLElement>): void;
  onPointerUp?(): void;
  onPointerCancel?(): void;
} {
  const atual = useRef(render);
  atual.current = render;
  const timer = useRef(0);
  const limpar = () => window.clearTimeout(timer.current);
  useEffect(() => () => {
    window.clearTimeout(timer.current);
  }, []);
  const abrir = (el: HTMLElement) => { if (atual.current) abrirDica(el, () => atual.current?.() ?? null); };

  const base: HandlersDaDica = {
    onPointerEnter(e) {
      if (e.pointerType !== 'mouse' || !atual.current) return;
      const el = e.currentTarget;
      limpar();
      timer.current = window.setTimeout(() => abrir(el), ATRASO_MOUSE);
    },
    onPointerLeave(e) {
      limpar();
      fecharDica(e.currentTarget);
    },
    onFocus(e) {
      // Só o foco do teclado (o clique também foca): :focus-visible.
      if (!e.currentTarget.matches(':focus-visible')) return;
      abrir(e.currentTarget);
    },
    onBlur(e) { fecharDica(e.currentTarget); }
  };
  if (!toque) return base;
  return {
    ...base,
    onPointerDown(e) {
      if (e.pointerType !== 'touch' || !atual.current) return;
      const el = e.currentTarget;
      limpar();
      timer.current = window.setTimeout(() => {
        abrir(el);
        // O toque que abriu não vira clique.
        const engole = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
        el.addEventListener('click', engole, { capture: true, once: true });
        window.setTimeout(() => el.removeEventListener('click', engole, { capture: true }), 600);
      }, TOQUE_LONGO);
    },
    onPointerUp: limpar,
    onPointerCancel: limpar
  };
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => { ouvintes.delete(f); };
}
const ler = () => estado;

/** A caixa, uma só, no body. Monte uma vez (a SkillBar monta). */
export function DicaHost() {
  const e = useSyncExternalStore(assinar, ler, ler);
  const caixa = useRef<HTMLDivElement>(null);
  const [, setTique] = useState(0);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  // Repinta a cada segundo (o tempo do efeito) e fecha se o elemento saiu da tela.
  useEffect(() => {
    if (!e.el) return;
    const id = window.setInterval(() => {
      if (!e.el?.isConnected) fecharDica(e.el ?? undefined);
      else setTique(n => n + 1);
    }, 1000);
    const fechaToque = (ev: globalThis.PointerEvent) => {
      if (ev.pointerType === 'touch' && !e.el?.contains(ev.target as Node)) fecharDica();
    };
    const fecha = () => fecharDica();
    window.addEventListener('pointerdown', fechaToque, true);
    window.addEventListener('resize', fecha);
    window.addEventListener('wheel', fecha, { passive: true });
    return () => {
      window.clearInterval(id);
      window.removeEventListener('pointerdown', fechaToque, true);
      window.removeEventListener('resize', fecha);
      window.removeEventListener('wheel', fecha);
    };
  }, [e.el, e.v]);

  useLayoutEffect(() => {
    if (!e.el || !caixa.current) { setPos(null); return; }
    const r = e.el.getBoundingClientRect();
    const w = caixa.current.offsetWidth;
    const h = caixa.current.offsetHeight;
    let x = r.left + r.width / 2 - w / 2;
    let y = r.top - h - 8;
    if (y < 6) y = r.bottom + 8;
    x = Math.max(6, Math.min(window.innerWidth - w - 6, x));
    y = Math.max(6, Math.min(window.innerHeight - h - 6, y));
    setPos(p => (p && p.x === x && p.y === y ? p : { x, y }));
  });

  if (!e.el || !e.render) return null;
  const conteudo = e.render();
  if (!conteudo) return null;
  return createPortal(
    <div
      ref={caixa}
      className="dk-host"
      role="tooltip"
      style={pos ? { left: pos.x, top: pos.y } : { left: -9999, top: -9999 }}
    >
      {conteudo}
    </div>,
    document.body
  );
}
